import React, { useState } from 'react';
import { 
  Book, ChevronRight, ChevronDown, Search, Home, ShoppingCart, Package, 
  Users, CreditCard, Truck, Settings, BarChart3, Printer, Shield, 
  ClipboardList, Eye, Gift, Palette, Database, Box, CheckCircle,
  AlertTriangle, Info, Lightbulb, ArrowRight, ExternalLink,
  Receipt, MapPin, Bell, QrCode, Scan, Store, Calculator, Play, Image
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import BackButton from '../components/BackButton';

// Operations Manual images
const manualImages = {
  admin_dashboard: 'https://static.prod-images.emergentagent.com/jobs/1fe5d863-25fb-4538-b1ee-307146fa8809/images/8c7461d157987f462fdc7f162491dde6b7372ec3bf3f6bd527cd07188dc89b72.png',
  pos_cashier: 'https://static.prod-images.emergentagent.com/jobs/1fe5d863-25fb-4538-b1ee-307146fa8809/images/e7722c85cefd81bfef078de861e8bd2441ec39c4db028ef40589b8e1e7322149.png',
  order_management: 'https://static.prod-images.emergentagent.com/jobs/1fe5d863-25fb-4538-b1ee-307146fa8809/images/445e3e290c6bab9c31351b3cdad6ef8645c70000412a69710a88ad8c9f1d2064.png',
  product_inventory: 'https://static.prod-images.emergentagent.com/jobs/1fe5d863-25fb-4538-b1ee-307146fa8809/images/3cf3acf5442c9fbe1f527a381462be10c4dc6a1741a4c01c61057b45acfa9ad4.png',
};

// Video placeholder component
const VideoPlaceholder = ({ title }) => (
  <div className="bg-gray-900 rounded-lg p-6 mb-4 flex flex-col items-center justify-center min-h-[150px]">
    <Play className="w-12 h-12 text-gray-500 mb-2" />
    <p className="text-gray-400 font-medium">{title}</p>
    <p className="text-gray-500 text-sm">Video tutorial coming soon</p>
  </div>
);

const OperationsManual = ({ onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [activeSection, setActiveSection] = useState('getting-started');

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
    setActiveSection(sectionId);
  };

  const manualSections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Home,
      color: 'bg-blue-500',
      content: [
        {
          title: 'Welcome to UPLANDS FOOD MARKET Admin Portal',
          type: 'intro',
          text: `This operations manual provides comprehensive guidance for administrators managing the UPLANDS FOOD MARKET e-commerce platform. As an administrator, you have access to powerful tools for managing products, orders, users, payments, and more.`
        },
        {
          title: 'Logging In',
          type: 'steps',
          steps: [
            'Navigate to the UPLANDS FOOD MARKET website',
            'Click the "Sign In" button in the top right corner',
            'Enter your administrator email and password',
            'Upon successful login, you\'ll see the ADMIN badge next to your profile',
            'The admin navigation bar will appear below the main navigation'
          ]
        },
        {
          title: 'Admin Navigation Overview',
          type: 'info',
          text: `The admin toolbar displays quick access buttons for all administrative functions. You can scroll left/right on the toolbar to see all options. Key sections include: POS/Cashier, Dashboard, Reports, Products, Orders, Users, and System Settings.`
        },
        {
          title: 'Quick Access Buttons',
          type: 'list',
          items: [
            { name: 'POS / CASHIER', desc: 'Point of Sale system for in-store transactions', color: 'emerald' },
            { name: 'Dashboard', desc: 'Overview of sales, orders, and key metrics', color: 'blue' },
            { name: 'Reports', desc: 'Detailed analytics and business reports', color: 'purple' },
            { name: 'Print Center', desc: 'Generate and print inventory/sales reports', color: 'blue' }
          ]
        },
        {
          title: 'Admin Dashboard Overview',
          type: 'image',
          src: manualImages.admin_dashboard,
          caption: 'Main admin dashboard with sales metrics and quick actions'
        },
        {
          title: 'Getting Started Tutorial',
          type: 'video'
        }
      ]
    },
    {
      id: 'pos-cashier',
      title: 'POS / Cashier Operations',
      icon: ShoppingCart,
      color: 'bg-emerald-500',
      content: [
        {
          title: 'Point of Sale Overview',
          type: 'intro',
          text: `The POS (Point of Sale) system allows cashiers to process in-store purchases efficiently. It supports barcode scanning, manual product search, multiple payment methods, and receipt printing.`
        },
        {
          title: 'Processing a Sale',
          type: 'steps',
          steps: [
            'Click "POS / CASHIER" from the admin toolbar',
            'Scan product barcodes using the camera scanner or enter barcode manually',
            'Alternatively, search for products by name using the search bar',
            'Adjust quantities as needed using +/- buttons',
            'Review the cart total (subtotal, tax, and grand total)',
            'Select payment method: Cash, Card, or Gift Card',
            'Complete the transaction and print receipt if needed'
          ]
        },
        {
          title: 'Barcode Scanning',
          type: 'tip',
          text: `Click the "Scan" button to activate the camera scanner. Hold the product barcode in front of the camera until it beeps. The product will automatically be added to the cart.`
        },
        {
          title: 'Handling Returns',
          type: 'warning',
          text: `For returns, locate the original order in the Orders section, mark items as returned, and process the refund through the Payment Portal. Always verify the receipt before processing returns.`
        },
        {
          title: 'POS Sales Report',
          type: 'info',
          text: `Access the "POS Sales Report" to view daily sales summaries, top-selling products, hourly sales breakdown, and cashier performance metrics.`
        },
        {
          title: 'POS Interface',
          type: 'image',
          src: manualImages.pos_cashier,
          caption: 'Point of Sale interface for processing transactions'
        },
        {
          title: 'POS Operations Tutorial',
          type: 'video'
        }
      ]
    },
    {
      id: 'product-management',
      title: 'Product Management',
      icon: Box,
      color: 'bg-orange-500',
      content: [
        {
          title: 'Managing Your Product Catalog',
          type: 'intro',
          text: `The Products section allows you to add, edit, and manage your entire product inventory. You can update prices, stock levels, images, and product details.`
        },
        {
          title: 'Adding a New Product',
          type: 'steps',
          steps: [
            'Navigate to "Products" in the admin toolbar',
            'Click the "Add Product" button',
            'Fill in product details: Name, Price, Category, Barcode',
            'Upload a product image (recommended: square format, min 500x500px)',
            'Set initial stock quantity and reorder level',
            'Mark as "Taxable" if applicable (beverages, snacks)',
            'Click "Save" to add the product'
          ]
        },
        {
          title: 'Bulk Product Import',
          type: 'info',
          text: `You can import products in bulk using JSON format. Go to System Maintenance > Backup & Restore to import product data. The import will match products by barcode and update existing entries.`
        },
        {
          title: 'Managing Stock Levels',
          type: 'steps',
          steps: [
            'Find the product in the Products list',
            'Click "Edit" or the product card',
            'Update the "Quantity" field',
            'Set "Reorder Level" to get low-stock alerts',
            'Save changes'
          ]
        },
        {
          title: 'Product Categories',
          type: 'list',
          items: [
            { name: 'Produce', desc: 'Fresh fruits and vegetables', color: 'green' },
            { name: 'Meats', desc: 'Fresh and frozen meats', color: 'red' },
            { name: 'Seafood', desc: 'Fish and seafood products', color: 'blue' },
            { name: 'Spices', desc: 'Seasonings and spices', color: 'orange' },
            { name: 'Grains', desc: 'Rice, flour, and grains', color: 'yellow' },
            { name: 'Beverages', desc: 'Drinks (taxable)', color: 'purple' },
            { name: 'Snacks', desc: 'Chips, cookies (taxable)', color: 'pink' }
          ]
        },
        {
          title: 'Duplicate Products',
          type: 'tip',
          text: `Use the "Duplicates" feature to find and merge similar products. This helps keep your catalog clean and prevents customer confusion.`
        },
        {
          title: 'Product Inventory Interface',
          type: 'image',
          src: manualImages.product_inventory,
          caption: 'Manage your entire product catalog from one place'
        },
        {
          title: 'Product Management Tutorial',
          type: 'video'
        }
      ]
    },
    {
      id: 'order-management',
      title: 'Order Management',
      icon: ClipboardList,
      color: 'bg-purple-500',
      content: [
        {
          title: 'Managing Customer Orders',
          type: 'intro',
          text: `The Orders section displays all customer orders with their current status. You can view order details, update status, and track fulfillment progress.`
        },
        {
          title: 'Order Statuses',
          type: 'list',
          items: [
            { name: 'Pending', desc: 'Order placed, awaiting confirmation', color: 'yellow' },
            { name: 'Payment Confirmed', desc: 'Payment received and verified', color: 'blue' },
            { name: 'Picking', desc: 'Staff is collecting items', color: 'orange' },
            { name: 'Picked', desc: 'All items collected and packed', color: 'indigo' },
            { name: 'Ready for Delivery', desc: 'Order ready for driver pickup', color: 'purple' },
            { name: 'Out for Delivery', desc: 'Driver is delivering the order', color: 'blue' },
            { name: 'Delivered', desc: 'Order successfully delivered', color: 'green' },
            { name: 'Ready for Pickup', desc: 'Customer can collect in-store', color: 'teal' },
            { name: 'Cancelled', desc: 'Order was cancelled', color: 'red' }
          ]
        },
        {
          title: 'Updating Order Status',
          type: 'steps',
          steps: [
            'Find the order in the Orders list',
            'Click on the order to view details',
            'Select the new status from the dropdown',
            'Add any notes if needed',
            'Click "Update Status" - customer will be notified via email'
          ]
        },
        {
          title: 'Order Types',
          type: 'info',
          text: `Orders can be: Delivery (shipped to customer), Pickup (customer collects in-store), or In-Store (purchased at POS). Each type has different fulfillment workflows.`
        },
        {
          title: 'Order Management Interface',
          type: 'image',
          src: manualImages.order_management,
          caption: 'View and manage all customer orders with status tracking'
        },
        {
          title: 'Order Processing Tutorial',
          type: 'video'
        }
      ]
    },
    {
      id: 'picking-portal',
      title: 'Picking Portal',
      icon: Package,
      color: 'bg-amber-500',
      content: [
        {
          title: 'Order Picking Process',
          type: 'intro',
          text: `The Picking Portal is used by staff to collect items for customer orders. It provides a streamlined interface for scanning and verifying products.`
        },
        {
          title: 'Using the Picking Portal',
          type: 'steps',
          steps: [
            'Open "Picking Portal" from the admin toolbar',
            'Select an order from the pending orders list',
            'The system displays all items to be picked',
            'Scan each product barcode or click to mark as picked',
            'Handle substitutions if items are unavailable',
            'Mark order as "Picked" when complete',
            'Print picking slip if needed'
          ]
        },
        {
          title: 'Picking List',
          type: 'info',
          text: `The "Picking List" feature shows a consolidated view of all items needed across multiple orders, organized by category and location for efficient picking.`
        },
        {
          title: 'Handling Out-of-Stock Items',
          type: 'warning',
          text: `If an item is unavailable, you can offer a substitution or partial refund. Always contact the customer before substituting items. Mark the item as "Unavailable" and add a note explaining the situation.`
        }
      ]
    },
    {
      id: 'payment-management',
      title: 'Payment Management',
      icon: CreditCard,
      color: 'bg-green-500',
      content: [
        {
          title: 'Payment Portal Overview',
          type: 'intro',
          text: `The Payment Portal allows you to manage all payment-related operations including confirming payments, processing refunds, and managing payment methods.`
        },
        {
          title: 'Confirming Payments',
          type: 'steps',
          steps: [
            'Go to "Confirm Payments" in the admin toolbar',
            'View orders pending payment confirmation',
            'Verify payment was received (check Stripe dashboard or bank)',
            'Click "Confirm Payment" to update order status',
            'Customer receives confirmation email automatically'
          ]
        },
        {
          title: 'Stripe Configuration',
          type: 'info',
          text: `Configure your Stripe settings in "Stripe Settings". You can view your API keys, test mode status, and configure webhooks. Stripe handles all card payments securely.`
        },
        {
          title: 'Accepted Payment Methods',
          type: 'list',
          items: [
            { name: 'Credit/Debit Card', desc: 'Visa, Mastercard, Amex via Stripe', color: 'blue' },
            { name: 'Cash', desc: 'In-store and delivery payments', color: 'green' },
            { name: 'Gift Cards', desc: 'UPLANDS FOOD MARKET gift cards', color: 'purple' },
            { name: 'E-Transfer', desc: 'For delivery orders', color: 'orange' }
          ]
        }
      ]
    },
    {
      id: 'user-management',
      title: 'User Management',
      icon: Users,
      color: 'bg-indigo-500',
      content: [
        {
          title: 'Managing Users',
          type: 'intro',
          text: `The User Management section allows you to view all registered users, manage their roles, and monitor their activity.`
        },
        {
          title: 'User Roles',
          type: 'list',
          items: [
            { name: 'Super Admin', desc: 'Full system access, can manage other admins', color: 'red' },
            { name: 'Admin', desc: 'Access to all admin features', color: 'purple' },
            { name: 'Staff', desc: 'POS, Picking, and basic operations', color: 'blue' },
            { name: 'Driver', desc: 'Delivery management and navigation', color: 'orange' },
            { name: 'Customer', desc: 'Shopping and order tracking', color: 'green' }
          ]
        },
        {
          title: 'Viewing User Details',
          type: 'steps',
          steps: [
            'Navigate to "Users" in the admin toolbar',
            'Search for a user by name or email',
            'Click the eye icon to view user audit trail',
            'See order history, login activity, and role changes',
            'Update user status if needed (Active/Suspended)'
          ]
        },
        {
          title: 'User Status Management',
          type: 'warning',
          text: `You can Activate, Deactivate, or Suspend users. Suspended users cannot log in. Deactivated users can be reactivated later. Always document the reason for status changes.`
        }
      ]
    },
    {
      id: 'driver-management',
      title: 'Driver Management',
      icon: Truck,
      color: 'bg-cyan-500',
      content: [
        {
          title: 'Managing Delivery Drivers',
          type: 'intro',
          text: `The Drivers Dashboard allows you to manage your delivery team, assign orders, track deliveries, and monitor driver performance.`
        },
        {
          title: 'Driver Onboarding',
          type: 'steps',
          steps: [
            'New drivers register with role "Driver"',
            'Admin reviews driver application in User Management',
            'Verify driver documents (license, insurance)',
            'Activate driver account',
            'Driver completes profile with vehicle details'
          ]
        },
        {
          title: 'Assigning Deliveries',
          type: 'info',
          text: `Orders marked as "Ready for Delivery" appear in the driver\'s queue. Drivers can accept orders based on their location and availability. You can also manually assign orders to specific drivers.`
        },
        {
          title: 'Driver QR Code Scanning',
          type: 'tip',
          text: `Drivers scan the order QR code at pickup to confirm they have received the order. This updates the status to "Out for Delivery" and notifies the customer.`
        }
      ]
    },
    {
      id: 'reports-analytics',
      title: 'Reports & Analytics',
      icon: BarChart3,
      color: 'bg-rose-500',
      content: [
        {
          title: 'Business Reports',
          type: 'intro',
          text: `Access comprehensive reports to analyze your business performance. Reports include sales data, inventory status, customer insights, and profitability analysis.`
        },
        {
          title: 'Available Reports',
          type: 'list',
          items: [
            { name: 'Sales Report', desc: 'Daily, weekly, monthly sales with trends', color: 'green' },
            { name: 'Stock Report', desc: 'Inventory levels and low-stock alerts', color: 'orange' },
            { name: 'Best Sellers', desc: 'Top performing products', color: 'blue' },
            { name: 'Profitability', desc: 'Revenue, costs, and profit margins', color: 'purple' },
            { name: 'POS Sales', desc: 'In-store transaction summaries', color: 'emerald' }
          ]
        },
        {
          title: 'Print Center',
          type: 'steps',
          steps: [
            'Go to "Print Center" in the admin toolbar',
            'Select report type: Products In Stock or Sales Report',
            'Choose date range for sales reports',
            'Preview the report on screen',
            'Click "Print Report" to print or "Save as PDF" to download'
          ]
        },
        {
          title: 'General Overseer',
          type: 'info',
          text: `The Overseer dashboard provides a real-time overview of store operations including active orders, staff activity, and key performance indicators.`
        }
      ]
    },
    {
      id: 'branding-settings',
      title: 'Branding & Settings',
      icon: Palette,
      color: 'bg-pink-500',
      content: [
        {
          title: 'Customizing Your Store',
          type: 'intro',
          text: `The Branding Settings allow you to customize the look and feel of your store including logo, colors, banners, and business information.`
        },
        {
          title: 'Branding Options',
          type: 'list',
          items: [
            { name: 'Business Name', desc: 'Store name displayed in header', color: 'blue' },
            { name: 'Tagline', desc: 'Short description under the name', color: 'purple' },
            { name: 'Logo', desc: 'Upload your store logo', color: 'green' },
            { name: 'Banner Image', desc: 'Hero banner on homepage', color: 'orange' },
            { name: 'Colors', desc: 'Primary, secondary, accent colors', color: 'pink' }
          ]
        },
        {
          title: 'Updating Branding',
          type: 'steps',
          steps: [
            'Navigate to "Branding" in the admin toolbar',
            'Upload new logo or banner images',
            'Update business name and tagline',
            'Adjust color scheme using color pickers',
            'Preview changes before saving',
            'Click "Save Settings" to apply changes'
          ]
        }
      ]
    },
    {
      id: 'promotions',
      title: 'Promotions & Marketing',
      icon: Gift,
      color: 'bg-violet-500',
      content: [
        {
          title: 'Managing Promotions',
          type: 'intro',
          text: `The Promotions section allows you to create and manage special offers, discounts, and marketing campaigns to attract and retain customers.`
        },
        {
          title: 'Creating a Promotion',
          type: 'steps',
          steps: [
            'Go to "Promotions" in the admin toolbar',
            'Click "Create New Promotion"',
            'Set promotion type (Percentage off, Fixed discount, BOGO)',
            'Define applicable products or categories',
            'Set start and end dates',
            'Create promotional notification for customers',
            'Activate the promotion'
          ]
        },
        {
          title: 'Push Notifications',
          type: 'info',
          text: `Send push notifications to customers about new promotions, flash sales, or important updates. Notifications appear on their devices if they have enabled notifications.`
        },
        {
          title: 'Gift Cards',
          type: 'tip',
          text: `Gift cards are a great promotional tool. You can create and sell digital gift cards that customers can use for purchases or gift to others.`
        }
      ]
    },
    {
      id: 'system-maintenance',
      title: 'System Maintenance',
      icon: Settings,
      color: 'bg-gray-500',
      content: [
        {
          title: 'System Administration',
          type: 'intro',
          text: `The System Maintenance section provides tools for managing system settings, backups, device configurations, and overall system health.`
        },
        {
          title: 'Backup & Restore',
          type: 'steps',
          steps: [
            'Navigate to "Backup & Restore" in the admin toolbar',
            'Click "Create Backup" to backup current data',
            'Download backup file for safekeeping',
            'To restore, upload a backup file and click "Restore"',
            'Verify data after restoration'
          ]
        },
        {
          title: 'Device Settings',
          type: 'info',
          text: `Configure POS devices, printers, barcode scanners, and other hardware. Set up receipt templates and printer connections.`
        },
        {
          title: 'Role Management',
          type: 'list',
          items: [
            { name: 'Create Roles', desc: 'Define custom roles with specific permissions', color: 'blue' },
            { name: 'Assign Permissions', desc: 'Control access to features per role', color: 'purple' },
            { name: 'Audit Roles', desc: 'Track role changes and assignments', color: 'orange' }
          ]
        },
        {
          title: 'Data Export',
          type: 'warning',
          text: `Regularly export your data for backup purposes. Go to System Maintenance to export products, orders, and user data in JSON format.`
        }
      ]
    },
    {
      id: 'troubleshooting',
      title: 'Troubleshooting',
      icon: AlertTriangle,
      color: 'bg-red-500',
      content: [
        {
          title: 'Common Issues & Solutions',
          type: 'intro',
          text: `This section covers common issues you may encounter and their solutions.`
        },
        {
          title: 'Login Issues',
          type: 'list',
          items: [
            { name: 'Forgot Password', desc: 'Use "Forgot Password" link to reset via email', color: 'blue' },
            { name: 'Account Locked', desc: 'Contact super admin to unlock account', color: 'red' },
            { name: 'Wrong Password', desc: 'Check caps lock, try password reset', color: 'orange' }
          ]
        },
        {
          title: 'Payment Issues',
          type: 'list',
          items: [
            { name: 'Card Declined', desc: 'Customer should try another card or contact bank', color: 'red' },
            { name: 'Payment Not Showing', desc: 'Check Stripe dashboard, may take 1-2 minutes', color: 'orange' },
            { name: 'Refund Failed', desc: 'Verify original payment exists, check Stripe logs', color: 'yellow' }
          ]
        },
        {
          title: 'Scanner Not Working',
          type: 'steps',
          steps: [
            'Ensure camera permissions are granted in browser',
            'Check that you\'re using HTTPS (required for camera access)',
            'Try refreshing the page',
            'Clear browser cache and try again',
            'Use manual barcode entry as fallback'
          ]
        },
        {
          title: 'Getting Help',
          type: 'info',
          text: `If you encounter issues not covered here, contact technical support with details about the problem, screenshots if possible, and steps to reproduce the issue.`
        }
      ]
    }
  ];

  const filteredSections = manualSections.filter(section =>
    section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    section.content.some(item => 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.text && item.text.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  );

  const renderContent = (content) => {
    return content.map((item, index) => {
      switch (item.type) {
        case 'intro':
          return (
            <div key={index} className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">{item.title}</h4>
              <p className="text-blue-800">{item.text}</p>
            </div>
          );
        case 'steps':
          return (
            <div key={index} className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {item.title}
              </h4>
              <ol className="space-y-2 ml-4">
                {item.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">
                      {i + 1}
                    </span>
                    <span className="text-gray-700 pt-0.5">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          );
        case 'list':
          return (
            <div key={index} className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3">{item.title}</h4>
              <div className="grid gap-2">
                {item.items.map((listItem, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <Badge className={`bg-${listItem.color}-100 text-${listItem.color}-700 border-0`}>
                      {listItem.name}
                    </Badge>
                    <span className="text-gray-600 text-sm">{listItem.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        case 'info':
          return (
            <div key={index} className="bg-gray-50 border border-gray-200 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                {item.title}
              </h4>
              <p className="text-gray-700">{item.text}</p>
            </div>
          );
        case 'tip':
          return (
            <div key={index} className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-green-500" />
                {item.title}
              </h4>
              <p className="text-green-800">{item.text}</p>
            </div>
          );
        case 'warning':
          return (
            <div key={index} className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {item.title}
              </h4>
              <p className="text-amber-800">{item.text}</p>
            </div>
          );
        case 'image':
          return (
            <div key={index} className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Image className="w-5 h-5 text-blue-500" />
                {item.title}
              </h4>
              <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                <img src={item.src} alt={item.title} className="w-full h-auto" loading="lazy" />
              </div>
              {item.caption && <p className="text-gray-500 text-sm mt-2 text-center">{item.caption}</p>}
            </div>
          );
        case 'video':
          return (
            <div key={index} className="mb-6">
              <VideoPlaceholder title={item.title} />
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <BackButton onClick={onBack} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Book className="w-7 h-7 text-blue-600" />
                Operations Manual
              </h1>
              <p className="text-gray-500 text-sm">Comprehensive guide for administrators</p>
            </div>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search manual..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="manual-search"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-4">Contents</h3>
              <nav className="space-y-1">
                {filteredSections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => toggleSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        isActive 
                          ? 'bg-blue-50 text-blue-700' 
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                      data-testid={`nav-${section.id}`}
                    >
                      <div className={`p-1.5 rounded ${section.color}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium flex-1">{section.title}</span>
                      {isActive ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {filteredSections.map((section) => {
              const Icon = section.icon;
              const isExpanded = expandedSections.includes(section.id);
              
              return (
                <Card key={section.id} className="overflow-hidden" id={section.id}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center gap-4 p-4 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className={`p-3 rounded-lg ${section.color}`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h2 className="text-xl font-bold text-gray-900">{section.title}</h2>
                      <p className="text-sm text-gray-500">{section.content.length} topics</p>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-6 h-6 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-6 h-6 text-gray-400" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="p-6 pt-2 border-t border-gray-100">
                      {renderContent(section.content)}
                    </div>
                  )}
                </Card>
              );
            })}

            {filteredSections.length === 0 && (
              <Card className="p-8 text-center">
                <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900">No results found</h3>
                <p className="text-gray-500">Try a different search term</p>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 mt-8 py-6">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">
            UPLANDS FOOD MARKET Operations Manual v1.0 | Last Updated: {new Date().toLocaleDateString()}
          </p>
          <p className="text-gray-400 text-xs mt-1">
            For technical support, contact your system administrator
          </p>
        </div>
      </div>
    </div>
  );
};

export default OperationsManual;
